"use client"

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import DashboardLayout from "../../components/Layout/DashboardLayout"
import Breadcrumbs from "../../components/Layout/Breadcrumbs"
import MiniFooter from "../../components/Layout/miniFooter"
import { Card, Form, Button, Alert, Spinner } from "react-bootstrap"
import { FaGlobe, FaLink, FaHome, FaArrowLeft, FaCheck, FaExclamationTriangle, FaCheckCircle } from "react-icons/fa"
import api from "../../services/api"

export default function AddDomainPage() {
  const navigate = useNavigate()
  const breadcrumbItems = [
    { name: "Dashboard", path: "/user-dashboard" },
    { name: "Branded Domains", path: "/dashboard/custom-domains" },
    { name: "Add New Domain" },
  ]

  const [domain, setDomain] = useState("")
  const [redirect404, setRedirect404] = useState("")
  const [baseRedirect, setBaseRedirect] = useState("")
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState("")

  const handleSubmit = async (e) => {
    e.preventDefault()
    setMessage("")
    setLoading(true)

    try {
      const payload = {
        domain,
        ...(baseRedirect && { baseRedirect }),
        ...(redirect404 && { redirect404 })
      };

      await api.post("/domains", payload)

      setMessage({ type: "success", text: "Domain added successfully!" })
      setTimeout(() => navigate("/dashboard/custom-domains"), 2000)
    } catch (err) {
      console.error(err)
      setMessage({
        type: "danger",
        text: err.response?.data?.message || "Failed to add domain.",
      })
    } finally {
      setLoading(false)
    }
  }

  const inputStyle = {
    paddingLeft: "44px",
    height: "48px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    fontSize: "15px",
    transition: "all 0.2s ease",
  }

  const iconStyle = {
    position: "absolute",
    left: "14px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#64748b",
    zIndex: 2,
  }

  return (
    <DashboardLayout>
      <style>{`
        .add-domain-page {
          background: #f8fafc;
          min-height: 100vh;
        }
        .form-input:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          outline: none;
        }
        .prerequisites-card {
          background: #f0f9ff;
          border: 1px solid #bae6fd;
          border-radius: 12px;
        }
        .form-card {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
        }
        .btn-submit {
          background: #3b82f6;
          border: none;
          padding: 12px 28px;
          border-radius: 8px;
          font-weight: 600;
          transition: all 0.2s ease;
        }
        .btn-submit:hover:not(:disabled) {
          background: #2563eb;
        }
        .btn-submit:disabled {
          background: #94a3b8;
        }
        .btn-cancel {
          background: white;
          border: 1px solid #e2e8f0;
          color: #64748b;
          padding: 12px 28px;
          border-radius: 8px;
          font-weight: 600;
          transition: all 0.2s ease;
        }
        .btn-cancel:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
          color: #475569;
        }
      `}</style>

      <div className="add-domain-page">
        <div className="container py-4">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="mb-4">
            <h3 style={{ color: "#1e293b", fontWeight: "700", marginBottom: "4px" }}>Add New Domain</h3>
            <p style={{ color: "#64748b", margin: 0 }}>
              Connect your custom domain to start creating branded short links
            </p>
          </div>

          <Card className="prerequisites-card mb-4">
            <Card.Body className="p-4">
              <h5
                style={{
                  fontWeight: "600",
                  color: "#0369a1",
                  marginBottom: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FaCheckCircle />
                Prerequisites
              </h5>
              <ol style={{ color: "#0c4a6e", lineHeight: "2", paddingLeft: "20px", margin: 0 }}>
                <li>
                  Add a <strong>CNAME record</strong> in your domain registrar pointing to{" "}
                  <code style={{ background: "#e0f2fe", padding: "2px 8px", borderRadius: "4px", color: "#0369a1" }}>
                    domains.u2lr.com
                  </code>
                </li>
                <li>
                  If your domain is already used for website/email, use a subdomain (e.g.,{" "}
                  <code style={{ background: "#e0f2fe", padding: "2px 8px", borderRadius: "4px", color: "#0369a1" }}>
                    links.yourdomain.com
                  </code>
                  )
                </li>
                <li>After adding the DNS record, submit your custom domain here</li>
                <li>
                  Click <strong>Show Details</strong> on the domain list page to view SSL setup instructions
                </li>
              </ol>
            </Card.Body>
          </Card>

          <Card className="form-card">
            <Card.Body className="p-4">
              <h5 style={{ fontWeight: "600", color: "#1e293b", marginBottom: "24px" }}>Domain Information</h5>

              {message && (
                <Alert
                  variant={message.type}
                  style={{
                    borderRadius: "8px",
                    border: "none",
                    background: message.type === "success" ? "#dcfce7" : "#fef2f2",
                    color: message.type === "success" ? "#16a34a" : "#dc2626",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  {message.type === "success" ? <FaCheckCircle /> : <FaExclamationTriangle />}
                  {message.text}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-4">
                  <Form.Label style={{ fontWeight: "500", color: "#374151", marginBottom: "8px" }}>
                    Custom Domain Name <span style={{ color: "#dc2626" }}>*</span>
                  </Form.Label>
                  <div style={{ position: "relative" }}>
                    <FaGlobe style={iconStyle} />
                    <Form.Control
                      type="text"
                      className="form-input"
                      placeholder="example.com or links.example.com"
                      value={domain}
                      onChange={(e) => setDomain(e.target.value)}
                      required
                      style={inputStyle}
                    />
                  </div>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{ fontWeight: "500", color: "#374151", marginBottom: "8px" }}>
                    404 Redirect URL <span style={{ color: "#94a3b8", fontWeight: "400" }}>(optional)</span>
                  </Form.Label>
                  <div style={{ position: "relative" }}>
                    <FaLink style={iconStyle} />
                    <Form.Control
                      type="text"
                      className="form-input"
                      placeholder="https://example.com/404"
                      value={redirect404}
                      onChange={(e) => setRedirect404(e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                  <Form.Text style={{ color: "#64748b", fontSize: "13px" }}>
                    Where to redirect users when a short link is not found
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{ fontWeight: "500", color: "#374151", marginBottom: "8px" }}>
                    Base/TLD Redirect URL <span style={{ color: "#94a3b8", fontWeight: "400" }}>(optional)</span>
                  </Form.Label>
                  <div style={{ position: "relative" }}>
                    <FaHome style={iconStyle} />
                    <Form.Control
                      type="text"
                      className="form-input"
                      placeholder="https://example.com"
                      value={baseRedirect}
                      onChange={(e) => setBaseRedirect(e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                  <Form.Text style={{ color: "#64748b", fontSize: "13px" }}>
                    Where to redirect when users visit your domain root
                  </Form.Text>
                </Form.Group>

                <div className="d-flex justify-content-end gap-3">
                  <Button className="btn-cancel" onClick={() => navigate("/custom-domains")} disabled={loading}>
                    <FaArrowLeft className="me-2" />
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-submit" disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <FaCheck className="me-2" />
                        Add Domain
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </div>

        <MiniFooter />
      </div>
    </DashboardLayout>
  )
}
